import React, { useState, useEffect } from 'react';
import {
    ShieldExclamationIcon,
    XMarkIcon,
    TrashIcon,
    CheckCircleIcon,
    UserMinusIcon,
    EyeIcon,
    FlagIcon,
    ClockIcon
} from '@heroicons/react/24/outline';
import { getReportedContent, resolveReport } from '../api';
import { db } from '../firebase';
import { getDoc, doc } from 'firebase/firestore';
import { formatDistanceToNow } from 'date-fns';

const REASON_LABELS = {
    inappropriate: 'Inappropriate Content',
    spam: 'Spam',
    harassment: 'Harassment',
    hate_speech: 'Hate Speech',
    violence: 'Violence/Threats',
    misinformation: 'Misinformation',
    abusive_behavior: 'Abusive Behavior',
    other: 'Other Violation'
};

const ACTION_OPTIONS = [
    { value: 'no_action', label: 'No Action Needed', color: 'green' },
    { value: 'warning_issued', label: 'Warning Issued', color: 'yellow' },
    { value: 'content_removed', label: 'Content Removed', color: 'red' },
    { value: 'user_banned', label: 'User Banned', color: 'purple' }
];

const ModerationPanel = ({ isOpen, onClose }) => {
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('pending');
    const [selectedReport, setSelectedReport] = useState(null);
    const [contentPreview, setContentPreview] = useState(null);
    const [resolving, setResolving] = useState(false);

    useEffect(() => {
        if (isOpen) {
            loadReports();
        }
    }, [isOpen, filter]);

    const loadReports = async () => {
        setLoading(true);
        try {
            const data = await getReportedContent(filter, 100);
            setReports(data);
        } catch (e) {
            console.error("Error loading reports:", e);
        }
        setLoading(false);
    };

    const loadContentPreview = async (report) => {
        if (report.contentType === 'post') {
            try {
                const postDoc = await getDoc(doc(db, 'posts', report.contentId));
                if (postDoc.exists()) {
                    setContentPreview({ ...postDoc.data(), id: postDoc.id });
                } else {
                    setContentPreview({ deleted: true });
                }
            } catch (e) {
                setContentPreview({ error: true });
            }
        } else if (report.contentType === 'user') {
            try {
                const userDoc = await getDoc(doc(db, 'users', report.contentId));
                if (userDoc.exists()) {
                    setContentPreview({ ...userDoc.data(), uid: userDoc.id });
                } else {
                    setContentPreview({ deleted: true });
                }
            } catch (e) {
                setContentPreview({ error: true });
            }
        }
    };

    const handleSelectReport = (report) => {
        setSelectedReport(report);
        setContentPreview(null);
        loadContentPreview(report);
    };

    const handleResolve = async (actionTaken, shouldBanUser = false) => {
        if (!selectedReport) return;

        const confirmMsg = shouldBanUser
            ? 'This will BAN the user and remove their content. Continue?'
            : 'Resolve this report with action: ' + actionTaken + '?';

        if (!window.confirm(confirmMsg)) return;

        setResolving(true);
        try {
            await resolveReport(selectedReport.id, actionTaken, shouldBanUser);
            alert('Report resolved successfully');
            setSelectedReport(null);
            setContentPreview(null);
            loadReports();
        } catch (e) {
            alert('Error resolving report: ' + e.message);
        }
        setResolving(false);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6 lg:p-8 bg-slate-900/90 backdrop-blur-sm animate-fade-in">
            <div className="bg-white w-full max-w-6xl h-[90vh] md:h-[85vh] rounded-2xl overflow-hidden shadow-2xl animate-slide-up border-2 border-slate-200 flex flex-col">

                {/* Header */}
                <div className="p-4 md:p-5 bg-slate-900 text-white flex justify-between items-center flex-shrink-0">
                    <div className="flex items-center">
                        <ShieldExclamationIcon className="w-6 h-6 md:w-7 md:h-7 mr-2 md:mr-3 text-red-400" />
                        <h2 className="text-base md:text-lg font-black uppercase tracking-wider">Content Moderation</h2>
                        <span className="ml-3 text-xs md:text-sm bg-red-500 text-white px-2 md:px-3 py-1 rounded-full font-bold">
                            {reports.filter(r => r.status === 'pending').length} Pending
                        </span>
                    </div>
                    <button onClick={onClose} className="p-2 md:p-2.5 hover:bg-slate-700 rounded-full transition-colors">
                        <XMarkIcon className="w-6 h-6 md:w-7 md:h-7" />
                    </button>
                </div>

                {/* Filter Tabs */}
                <div className="flex border-b border-slate-200 bg-slate-50 flex-shrink-0">
                    {['pending', 'resolved', 'all'].map(f => (
                        <button
                            key={f}
                            onClick={() => setFilter(f)}
                            className={`px-4 md:px-6 lg:px-8 py-3 md:py-4 text-xs md:text-sm font-black uppercase tracking-wider transition-colors ${
                                filter === f
                                    ? 'bg-white text-slate-900 border-b-2 border-slate-900'
                                    : 'text-slate-400 hover:text-slate-600'
                            }`}
                        >
                            {f}
                        </button>
                    ))}
                </div>

                <div className="flex flex-1 overflow-hidden">
                    {/* Reports List */}
                    <div className="w-full md:w-1/2 border-r border-slate-200 overflow-y-auto bg-white">
                        {loading ? (
                            <div className="p-6 md:p-8 text-center text-slate-400 text-sm md:text-base">Loading reports...</div>
                        ) : reports.length === 0 ? (
                            <div className="p-6 md:p-8 text-center text-slate-400 text-sm md:text-base">No reports found</div>
                        ) : (
                            reports.map(report => (
                                <div
                                    key={report.id}
                                    onClick={() => handleSelectReport(report)}
                                    className={`p-4 border-b border-slate-100 cursor-pointer transition-colors hover:bg-slate-50 ${
                                        selectedReport?.id === report.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                                    }`}
                                >
                                    <div className="flex items-start justify-between mb-2">
                                        <div className="flex items-center">
                                            <FlagIcon className={`w-4 h-4 mr-2 ${report.status === 'pending' ? 'text-red-500' : 'text-green-500'}`} />
                                            <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded ${
                                                report.contentType === 'post' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                                            }`}>
                                                {report.contentType}
                                            </span>
                                        </div>
                                        <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                                            report.status === 'pending'
                                                ? 'bg-red-100 text-red-700'
                                                : 'bg-green-100 text-green-700'
                                        }`}>
                                            {report.status}
                                        </span>
                                    </div>

                                    <div className="text-sm font-bold text-slate-900 mb-1">
                                        {REASON_LABELS[report.reason] || report.reason}
                                    </div>

                                    <div className="flex items-center text-xs text-slate-500">
                                        <ClockIcon className="w-3 h-3 mr-1" />
                                        {report.createdAt?.seconds
                                            ? formatDistanceToNow(new Date(report.createdAt.seconds * 1000), { addSuffix: true })
                                            : 'Recently'
                                        }
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {/* Report Details */}
                    <div className="hidden md:flex md:w-1/2 overflow-y-auto bg-slate-50 p-4 md:p-6">
                        {!selectedReport ? (
                            <div className="flex flex-col items-center justify-center h-full text-slate-400">
                                <EyeIcon className="w-12 h-12 md:w-16 md:h-16 mb-2 md:mb-3" />
                                <p className="text-sm md:text-base font-medium">Select a report to view details</p>
                            </div>
                        ) : (
                            <div className="space-y-6">
                                {/* Report Info */}
                                <div className="bg-white rounded-xl p-4 border border-slate-200">
                                    <h3 className="text-xs font-black uppercase text-slate-400 mb-3">Report Details</h3>
                                    <div className="space-y-2 text-sm">
                                        <div><span className="font-bold">Type:</span> {selectedReport.contentType}</div>
                                        <div><span className="font-bold">Reason:</span> {REASON_LABELS[selectedReport.reason] || selectedReport.reason}</div>
                                        <div><span className="font-bold">Reported By:</span> {selectedReport.reportedBy}</div>
                                        <div><span className="font-bold">Status:</span> {selectedReport.status}</div>
                                        {selectedReport.resolvedBy && (
                                            <>
                                                <div><span className="font-bold">Resolved By:</span> {selectedReport.resolvedBy}</div>
                                                <div><span className="font-bold">Action Taken:</span> {selectedReport.actionTaken}</div>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* Content Preview */}
                                <div className="bg-white rounded-xl p-4 border border-slate-200">
                                    <h3 className="text-xs font-black uppercase text-slate-400 mb-3">Content Preview</h3>
                                    {!contentPreview ? (
                                        <div className="text-sm text-slate-400">Loading...</div>
                                    ) : contentPreview.deleted ? (
                                        <div className="text-sm text-slate-400">Content has been deleted</div>
                                    ) : contentPreview.error ? (
                                        <div className="text-sm text-red-500">Error loading content</div>
                                    ) : selectedReport.contentType === 'post' ? (
                                        <div className="space-y-2">
                                            <div className="text-sm"><span className="font-bold">Author:</span> {contentPreview.uid}</div>
                                            <div className="bg-slate-50 p-3 rounded-lg text-sm">
                                                {contentPreview.body}
                                            </div>
                                            {contentPreview.sourceUrl && (
                                                <div className="text-xs text-blue-600 break-all">{contentPreview.sourceUrl}</div>
                                            )}
                                            <div className="text-xs text-slate-500">
                                                Flags: {contentPreview.flagCount || 0} |
                                                Upvotes: {contentPreview.upvotes || 0} |
                                                Downvotes: {contentPreview.downvotes || 0}
                                            </div>
                                        </div>
                                    ) : selectedReport.contentType === 'user' ? (
                                        <div className="space-y-2">
                                            <div className="text-sm"><span className="font-bold">Handle:</span> @{contentPreview.handle}</div>
                                            <div className="text-sm"><span className="font-bold">Email:</span> {contentPreview.email}</div>
                                            {contentPreview.bio && (
                                                <div className="bg-slate-50 p-3 rounded-lg text-sm">
                                                    {contentPreview.bio}
                                                </div>
                                            )}
                                            <div className="text-xs text-slate-500">
                                                Level: {contentPreview.stats?.level || 1} |
                                                Rep: {contentPreview.stats?.repTotal || 0}
                                            </div>
                                            {contentPreview.banned && (
                                                <div className="bg-red-50 border border-red-200 p-2 rounded text-xs text-red-700 font-bold">
                                                    USER IS BANNED
                                                </div>
                                            )}
                                        </div>
                                    ) : null}
                                </div>

                                {/* Actions */}
                                {selectedReport.status === 'pending' && (
                                    <div className="bg-white rounded-xl p-4 border border-slate-200">
                                        <h3 className="text-xs font-black uppercase text-slate-400 mb-3">Take Action</h3>
                                        <div className="space-y-2">
                                            {ACTION_OPTIONS.map(action => (
                                                <button
                                                    key={action.value}
                                                    onClick={() => handleResolve(
                                                        action.value,
                                                        action.value === 'user_banned'
                                                    )}
                                                    disabled={resolving}
                                                    className={`w-full py-3 px-4 rounded-lg text-sm font-bold uppercase tracking-wider transition-all disabled:opacity-50 flex items-center justify-center ${
                                                        action.color === 'green' ? 'bg-green-50 text-green-700 hover:bg-green-100' :
                                                        action.color === 'yellow' ? 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100' :
                                                        action.color === 'red' ? 'bg-red-50 text-red-700 hover:bg-red-100' :
                                                        'bg-purple-50 text-purple-700 hover:bg-purple-100'
                                                    }`}
                                                >
                                                    {action.value === 'no_action' && <CheckCircleIcon className="w-5 h-5 mr-2" />}
                                                    {action.value === 'content_removed' && <TrashIcon className="w-5 h-5 mr-2" />}
                                                    {action.value === 'user_banned' && <UserMinusIcon className="w-5 h-5 mr-2" />}
                                                    {action.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ModerationPanel;
